import * as _ from 'lodash-es';
import moment from 'moment';
import { Delta } from 'quill/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import type { SetNonNullable } from 'type-fest';
import type { AnnotationVersionChange } from './AnnotationVersionChangeCard';
import AnnotationVersionChangeCard from './AnnotationVersionChangeCard';
import type { ThreadVersionChange } from './ThreadVersionChangeCard';
import ThreadVersionChangeCard from './ThreadVersionChangeCard';
import type {
  MagneticWallFocus,
  MagneticWallHighlight,
} from '../../editor/editorSlice';
import { getClosestAttribute } from '../../editor/formats/utils/attributors';
import { useQuill } from '../../editor/QuillContext';
import type QuillEditor from '../../editor/QuillEditor';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import annotationThreadHash from '../../utils/url/annotationThreadHash';
import commentThreadHash from '../../utils/url/commentThreadHash';
import { getMagneticWallItemContentRects } from '../MagneticWall';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallLayout, {
  useMagneticWallLayout,
} from '../MagneticWallLayout';
import useMagneticWallItemSize from '../useMagneticWallItemSize';
import parseMagneticWallHash from '../utils/parseMagneticWallHash';
import toRelativeDOMRect from '../utils/toRelativeDOMRect';
import useLayoutSize, { LayoutSize } from '../utils/useLayoutSize';

const THREAD_FRAGMENT = gql(`
  fragment PostHistoryMagneticWall_thread on CommentThread {
    id
    ...ThreadVersionChangeCard_thread
  }
`);

const ANNOTATION_FRAGMENT = gql(`
  fragment PostHistoryMagneticWall_annotation on Annotation {
    id
    ...AnnotationVersionChangeCard_annotation
  }
`);

type PostHistoryMagneticWallVersionChanges =
  | ThreadVersionChange<DocumentType<typeof THREAD_FRAGMENT>>
  | AnnotationVersionChange<DocumentType<typeof ANNOTATION_FRAGMENT>>;

const getContentRectsForItemsWithoutContext = (
  itemsWithoutContext: PostHistoryMagneticWallVersionChanges[],
  quill: QuillEditor,
) => {
  const containerRect = quill.container.getBoundingClientRect();

  // This is to place the items without context and
  // without retain at the top of the magnetic wall
  const relativeZeroRect = toRelativeDOMRect(
    DOMRect.fromRect({
      x: containerRect.x,
      y: containerRect.y,
    }),
    containerRect,
  );

  return _.compact(
    itemsWithoutContext.map((item) => {
      if (item.type === 'comment') {
        const retainIndex = new Delta(
          new Delta(item.thread.context).filter((op) => op.retain != null),
        ).length();

        if (retainIndex === 0) {
          return {
            id: item.id,
            rect: relativeZeroRect,
          };
        }

        const [blot] = quill.getLine(Math.min(retainIndex, quill.getLength()));

        if (blot?.domNode instanceof Element) {
          return {
            id: item.id,
            rect: toRelativeDOMRect(
              blot.domNode.getBoundingClientRect(),
              containerRect,
            ),
          };
        }
      }

      return {
        id: item.id,
        rect: relativeZeroRect,
      };
    }),
  );
};

interface InnerPostHistoryMagneticWallProps {
  versionChanges: PostHistoryMagneticWallVersionChanges[];
  setMagneticWallFocus: (magneticWallFocus: MagneticWallFocus | null) => void;
  magneticWallFocus: MagneticWallFocus | null;
  setMagneticWallHighlight: (
    magneticWallHighlight: MagneticWallHighlight | null,
  ) => void;
  magneticWallItemSize: MagneticWallItemSize;
  canEdit: boolean;
}

const InnerPostHistoryMagneticWall = ({
  versionChanges,
  setMagneticWallFocus,
  magneticWallFocus,
  setMagneticWallHighlight,
  magneticWallItemSize,
  canEdit,
}: InnerPostHistoryMagneticWallProps) => {
  const { layout, debouncedUpdateDesiredPositions, updateThreadHeights } =
    useMagneticWallLayout();

  const positionedItems = useMemo(
    () =>
      versionChanges
        .map((item) => {
          return {
            ...item,
            top: layout.tops[item.id],
            isOverlapped: layout.overlappedIds.has(item.id),
          };
        })
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [layout.overlappedIds, layout.tops, versionChanges],
  );

  const hasPositionedItems = positionedItems.length > 0;
  const location = useLocation();

  useEffect(() => {
    if (!hasPositionedItems) return;

    const parseResult = parseMagneticWallHash(location.hash);
    if (!parseResult) return;

    setMagneticWallFocus({
      active: true,
      id: parseResult.id,
      type: parseResult.type,
      hasExpandIntent: true,
    });
  }, [hasPositionedItems, location.hash, setMagneticWallFocus]);

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    updateThreadHeights();
  }, [positionedItems, debouncedUpdateDesiredPositions, updateThreadHeights]);

  return (
    <ClickOutsideHandler
      onClickOutside={(e) => {
        const { target } = e;

        if (
          target instanceof Element &&
          // clicking on a comment / annotation within the post
          // shouldn't blur the magneticWallFocus, as it's selecting it
          getClosestAttribute(target) != null
        ) {
          return;
        }

        setMagneticWallFocus(null);
      }}
    >
      {positionedItems.map((versionChange) => {
        const copyLink = (hash: string) => {
          const link = new URL(window.location.href);
          link.hash = hash;
          navigator.clipboard.writeText(link.toString());
        };

        if (versionChange.type === 'annotation') {
          const isFocused = versionChange.id === magneticWallFocus?.id;

          return (
            <MagneticWallLayout.Item
              id={versionChange.id}
              focused={isFocused}
              top={versionChange.top}
              key={versionChange.id}
            >
              <AnnotationVersionChangeCard
                focused={isFocused}
                shouldOpenPopOver={
                  (isFocused && magneticWallFocus?.active) ?? false
                }
                versionChange={versionChange}
                onFocus={() =>
                  setMagneticWallFocus({
                    id: versionChange.id,
                    type: 'annotation',
                    active: true,
                    hasExpandIntent: false,
                  })
                }
                onCopyLink={() =>
                  copyLink(annotationThreadHash(versionChange.id))
                }
                onRemoveHighlight={() => setMagneticWallHighlight(null)}
                onHighlight={() =>
                  setMagneticWallHighlight({
                    id: versionChange.id,
                    type: 'annotation',
                  })
                }
                magneticWallItemSize={magneticWallItemSize}
              />
            </MagneticWallLayout.Item>
          );
        }

        const isFocused = versionChange.id === magneticWallFocus?.id;
        return (
          <MagneticWallLayout.Item
            id={versionChange.id}
            focused={isFocused}
            top={versionChange.top}
            key={versionChange.id}
          >
            <ThreadVersionChangeCard
              focused={isFocused}
              shouldOpenPopOver={
                (isFocused && magneticWallFocus?.active) ?? false
              }
              versionChange={versionChange}
              magneticWallItemSize={magneticWallItemSize}
              canEdit={canEdit}
              onFocus={() =>
                setMagneticWallFocus({
                  id: versionChange.id,
                  type: 'comment',
                  active: true,
                  hasExpandIntent: false,
                })
              }
              onCopyLink={() => copyLink(commentThreadHash(versionChange.id))}
              onRemoveHighlight={() => setMagneticWallHighlight(null)}
              onHighlight={() =>
                setMagneticWallHighlight({
                  id: versionChange.id,
                  type: 'comment',
                })
              }
            />
          </MagneticWallLayout.Item>
        );
      })}
    </ClickOutsideHandler>
  );
};

interface PostHistoryMagneticWallProps {
  versionChanges: PostHistoryMagneticWallVersionChanges[];
  setMagneticWallFocus: (magneticWallFocus: MagneticWallFocus | null) => void;
  magneticWallFocus: MagneticWallFocus | null;
  setMagneticWallHighlight: (
    magneticWallHighlight: MagneticWallHighlight | null,
  ) => void;
  canEdit: boolean;
}

const PostHistoryMagneticWall = ({
  versionChanges,
  setMagneticWallFocus,
  magneticWallFocus,
  setMagneticWallHighlight,
  canEdit,
}: PostHistoryMagneticWallProps) => {
  const layoutSize = useLayoutSize();
  const { quill } = useQuill();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const magneticWallItemSize = useMagneticWallItemSize(container);

  if (
    layoutSize &&
    [LayoutSize.mobile, LayoutSize.tablet].includes(layoutSize)
  ) {
    return null;
  }

  return (
    <MagneticWallLayout.Root
      containerRef={setContainer}
      itemSize={magneticWallItemSize}
      magneticWallItems={versionChanges.toSorted((a, b) => {
        // This will make sure that resolved threads without context
        // are sorted by their resolvedAt date.
        if (
          a.type === 'comment' &&
          b.type === 'comment' &&
          a.thread.resolvedAt &&
          b.thread.resolvedAt
        ) {
          return moment(a.thread.resolvedAt).diff(moment(b.thread.resolvedAt));
        }

        return 0;
      })}
      focus={magneticWallFocus}
      getRelativeContentRects={() => {
        if (!quill) return [];

        const contentRects = getMagneticWallItemContentRects(quill);

        const idsWithContext = new Set(contentRects.map((rect) => rect.id));
        const itemsWithoutContext = versionChanges.filter(
          (item) => !idsWithContext.has(item.id),
        );
        const contentRectsForItemsWithoutContext =
          getContentRectsForItemsWithoutContext(itemsWithoutContext, quill);

        return [...contentRectsForItemsWithoutContext, ...contentRects];
      }}
    >
      <InnerPostHistoryMagneticWall
        versionChanges={versionChanges}
        setMagneticWallFocus={setMagneticWallFocus}
        magneticWallFocus={magneticWallFocus}
        setMagneticWallHighlight={setMagneticWallHighlight}
        magneticWallItemSize={magneticWallItemSize}
        canEdit={canEdit}
      />
    </MagneticWallLayout.Root>
  );
};

export default PostHistoryMagneticWall;
